import API from '../config/api';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import axios from 'axios';

import { Modal, Button, Form, InputGroup } from 'react-bootstrap';

import { toast } from 'react-toastify';
const PLACEHOLDER_IMG =
  'data:image/svg+xml;utf8,' +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="90" height="90" viewBox="0 0 90 90">
      <rect width="90" height="90" rx="14" fill="#f3ede1"/>
      <text x="45" y="58" font-size="34" text-anchor="middle" fill="#c2410c">📦</text>
    </svg>`
  );

/*
  Drag-and-drop product picker.

  Products are dragged from the grid into the trolley at the bottom, which
  holds up to `maxProducts` DIFFERENT products (the same limit the cart API
  enforces). HTML5 drag-and-drop doesn't work on touch devices, so every card
  also carries a tap-to-add button — the drag is the flourish, not the only
  way in.
*/
function TrolleyPicker({ show, onHide, onCartChanged }) {
  const [products, setProducts] = useState([]);

  const [cartItems, setCartItems] = useState([]);

  const [maxProducts, setMaxProducts] = useState(5);

  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState('');

  /* Product id currently being dragged */
  const [draggingId, setDraggingId] = useState(null);

  /* Trolley is a valid drop target right now */
  const [isOver, setIsOver] = useState(false);

  /* Drives the landing animation on the trolley + the new slot */
  const [justAddedId, setJustAddedId] = useState(null);

  const [addingId, setAddingId] = useState(null);

  /* Cart row currently being removed */
  const [removingId, setRemovingId] = useState(null);

  /* Product shown in the quick-view panel */
  const [preview, setPreview] = useState(null);

  /* Product images mid-flight into the trolley */
  const [flyers, setFlyers] = useState([]);

  const flyerId = useRef(0);

  /* Where the flight lands */
  const trolleyRef = useRef(null);

  /* Rect of the card that started the current drag, so a drop can launch the
     flight from where the product actually was. */
  const dragRectRef = useRef(null);

  /*
    Launch a copy of the product image and arc it into the trolley. Purely
    decorative — the add itself has already been fired.
  */
  const flyIntoTrolley = (fromRect, src) => {
    const trolley = trolleyRef.current;

    if (!fromRect || !trolley) return false;

    const reduced = window.matchMedia?.(
      '(prefers-reduced-motion: reduce)'
    )?.matches;

    if (reduced) return false;

    const target = trolley.getBoundingClientRect();

    const id = ++flyerId.current;

    setFlyers((prev) => [
      ...prev,
      {
        id,
        src,
        left: fromRect.left,
        top: fromRect.top,
        size: Math.min(fromRect.width, fromRect.height),
        dx:
          target.left +
          target.width / 2 -
          (fromRect.left + Math.min(fromRect.width, fromRect.height) / 2),
        dy:
          target.top +
          target.height / 2 -
          (fromRect.top + Math.min(fromRect.width, fromRect.height) / 2)
      }
    ]);

    window.setTimeout(
      () => setFlyers((prev) => prev.filter((f) => f.id !== id)),
      850
    );

    return true;
  };

  const userInfo = JSON.parse(localStorage.getItem('userInfo') || 'null');

  const loadCart = useCallback(async () => {
    if (!userInfo?._id) return;

    try {
      const { data } = await axios.get(`${API}/cart/${userInfo._id}`);
      setCartItems(data);
    } catch (error) {
      console.log(error);
    }
    /* userInfo is re-parsed each render; the id is what matters */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo?._id]);

  useEffect(() => {
    if (!show) return;

    setLoading(true);

    Promise.all([
      axios
        .get(`${API}/products`)
        .then(({ data }) => setProducts(data))
        .catch((error) => console.log(error)),
      axios
        .get(`${API}/cart/limit`)
        .then(({ data }) => data?.max && setMaxProducts(data.max))
        .catch((error) => console.log(error)),
      loadCart()
    ]).finally(() => setLoading(false));
  }, [show, loadCart]);

  const inTrolley = (productId) =>
    cartItems.some((item) => String(item.productId) === String(productId));

  const freeSlots = Math.max(0, maxProducts - cartItems.length);

  const isFull = freeSlots === 0;

  /* Adding a product already in the trolley tops it up and needs no slot */
  const canAdd = (product) =>
    product.stock > 0 && (inTrolley(product._id) || !isFull);

  const addProduct = async (product, fromRect = null) => {
    if (!userInfo) {
      toast.error('Please Login First');
      return;
    }

    if (product.stock <= 0) {
      toast.error('This product is out of stock');
      return;
    }

    if (!canAdd(product)) {
      toast.error(
        `Trolley full — up to ${maxProducts} different products per order`
      );
      return;
    }

    try {
      setAddingId(product._id);

      /* Fire the flight immediately so it plays while the request is in the
         air, rather than after it lands. */
      const flew = flyIntoTrolley(fromRect, product.image || PLACEHOLDER_IMG);

      await axios.post(`${API}/cart`, {
        userId: userInfo._id,
        productId: product._id,
        name: product.name,
        image: product.image,
        price: product.price,
        quantity: 1
      });

      await loadCart();

      onCartChanged?.();

      toast.success(`🛒 ${product.name} added`);

      /* Bounce the trolley when the flight actually arrives (the flyer
         reaches it at ~0.7s), not the moment the request resolves. */
      const impactDelay = flew ? 620 : 0;

      window.setTimeout(() => {
        setJustAddedId(product._id);

        window.setTimeout(() => setJustAddedId(null), 700);
      }, impactDelay);
    } catch (error) {
      console.log(error);

      toast.error(error.response?.data?.message || 'Could not add to trolley');
    } finally {
      setAddingId(null);
    }
  };

  /* Take a product back out of the trolley, freeing its slot */
  const removeFromTrolley = async (productId) => {
    const row = cartItems.find(
      (item) => String(item.productId) === String(productId)
    );

    if (!row) return;

    try {
      setRemovingId(row._id);

      await axios.delete(`${API}/cart/${row._id}`);

      await loadCart();

      onCartChanged?.();

      toast.info(`Removed ${row.name}`);
    } catch (error) {
      console.log(error);

      toast.error('Could not remove that product');
    } finally {
      setRemovingId(null);
    }
  };

  /* ---- Drag handlers ---- */
  const onDragStart = (event, product) => {
    if (!canAdd(product)) {
      event.preventDefault();
      return;
    }

    setDraggingId(product._id);

    /* Remember where the card was, so the drop can fly from there */
    const image = event.currentTarget.querySelector('.tp-card-img');
    dragRectRef.current = (image || event.currentTarget).getBoundingClientRect();

    event.dataTransfer.setData('text/plain', product._id);
    event.dataTransfer.effectAllowed = 'copy';
  };

  const onDragEnd = () => {
    setDraggingId(null);
    setIsOver(false);
  };

  const onTrolleyDragOver = (event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
    setIsOver(true);
  };

  const onTrolleyDrop = (event) => {
    event.preventDefault();
    setIsOver(false);

    const id = event.dataTransfer.getData('text/plain');

    const product = products.find((p) => String(p._id) === String(id));

    if (product) addProduct(product, dragRectRef.current);

    dragRectRef.current = null;
  };

  const keyword = search.trim().toLowerCase();

  const visible = products.filter(
    (product) =>
      !keyword ||
      product.name?.toLowerCase().includes(keyword) ||
      product.category?.toLowerCase().includes(keyword)
  );

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
      centered
      scrollable
      contentClassName="tp-modal"
    >
      <Modal.Header closeButton className="tp-head">
        <div className="d-flex align-items-center gap-3 flex-wrap">
          <Modal.Title className="tp-title">🛒 Add More Products</Modal.Title>
          <span className={`tp-count${isFull ? ' is-full' : ''}`}>
            {cartItems.length} / {maxProducts}
          </span>
        </div>
      </Modal.Header>

      <Modal.Body className="tp-body">
        {/* Quick view — sits over the grid so the trolley stays visible */}
        {preview && (
          <div className="tp-preview">
            <button
              type="button"
              className="tp-preview-back"
              onClick={() => setPreview(null)}
            >
              ← Back to products
            </button>

            <div className="tp-preview-body">
              <div className="tp-preview-img">
                <img
                  src={preview.image || PLACEHOLDER_IMG}
                  alt={preview.name}
                  onError={(event) => {
                    event.currentTarget.src = PLACEHOLDER_IMG;
                  }}
                />
              </div>

              <div className="tp-preview-info">
                <h5 className="tp-preview-name">{preview.name}</h5>

                <div className="d-flex align-items-center gap-2 flex-wrap mb-2">
                  {preview.category && (
                    <span className="tp-preview-cat">{preview.category}</span>
                  )}

                  {preview.stock > 0 ? (
                    <span className="tp-preview-stock">
                      {preview.stock <= 5
                        ? `⚠ Only ${preview.stock} left`
                        : '✓ In stock'}
                    </span>
                  ) : (
                    <span className="tp-preview-stock is-out">
                      Out of stock
                    </span>
                  )}

                  {inTrolley(preview._id) && (
                    <span className="tp-preview-in">✓ In your trolley</span>
                  )}
                </div>

                <div className="tp-preview-price">
                  ₹{Number(preview.price).toLocaleString('en-IN')}
                </div>

                {(preview.shortDescription || preview.description) && (
                  <p className="tp-preview-desc">
                    {preview.shortDescription || preview.description}
                  </p>
                )}

                {preview.highlights?.length > 0 && (
                  <ul className="tp-preview-points">
                    {preview.highlights.slice(0, 4).map((point, i) => (
                      <li key={i}>{point}</li>
                    ))}
                  </ul>
                )}

                <div className="d-flex gap-2 flex-wrap mt-3">
                  <Button
                    className="tp-preview-add"
                    disabled={!canAdd(preview) || addingId === preview._id}
                    onClick={(event) => {
                      const image = event.currentTarget
                        .closest('.tp-preview')
                        ?.querySelector('.tp-preview-img');

                      addProduct(
                        preview,
                        image ? image.getBoundingClientRect() : null
                      );
                    }}
                  >
                    {preview.stock <= 0
                      ? 'Out of Stock'
                      : !canAdd(preview)
                        ? 'Trolley Full'
                        : inTrolley(preview._id)
                          ? '＋ Add One More'
                          : '🛒 Add to Trolley'}
                  </Button>

                  {inTrolley(preview._id) && (
                    <Button
                      className="tp-preview-remove"
                      onClick={() => removeFromTrolley(preview._id)}
                    >
                      ✕ Remove
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="tp-hint">
          <span className="tp-hint-drag">🖐 Drag a product into the trolley</span>
          <span className="tp-hint-or">or</span>
          <span>tap ＋ to add</span>
        </div>

        <InputGroup className="tp-search mb-3">
          <InputGroup.Text className="bg-white border-0">🔍</InputGroup.Text>
          <Form.Control
            className="border-0"
            placeholder="Search products..."
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </InputGroup>

        {loading ? (
          <div className="tp-grid">
            {Array.from({ length: 8 }).map((_, i) => (
              <div className="tp-card tp-skel-card" key={i}>
                <span className="tp-skel tp-skel-img" />
                <span className="tp-skel tp-skel-line" />
                <span className="tp-skel tp-skel-line short" />
              </div>
            ))}
          </div>
        ) : visible.length === 0 ? (
          <div className="tp-empty">
            <div style={{ fontSize: '2.6rem' }}>🔍</div>
            <div className="fw-bold">No products match “{search}”</div>
          </div>
        ) : (
          <div className="tp-grid">
            {visible.map((product) => {
              const already = inTrolley(product._id);

              const soldOut = product.stock <= 0;

              const blocked = !canAdd(product);

              return (
                <div
                  key={product._id}
                  className={`tp-card${
                    draggingId === product._id ? ' is-dragging' : ''
                  }${blocked ? ' is-blocked' : ''}${already ? ' is-in' : ''}`}
                  draggable={!blocked}
                  onDragStart={(event) => onDragStart(event, product)}
                  onDragEnd={onDragEnd}
                >
                  {already && <span className="tp-card-in">✓ In trolley</span>}

                  {soldOut && <span className="tp-card-out">Out of stock</span>}

                  <div className="tp-card-img">
                    <img
                      src={product.image || PLACEHOLDER_IMG}
                      alt={product.name}
                      draggable={false}
                      onError={(event) => {
                        event.currentTarget.src = PLACEHOLDER_IMG;
                      }}
                    />

                    {/* Inspect without leaving the picker */}
                    <button
                      type="button"
                      className="tp-eye"
                      title="Quick view"
                      onClick={() => setPreview(product)}
                    >
                      👁 Quick View
                    </button>
                  </div>

                  <div className="tp-card-name" title={product.name}>
                    {product.name}
                  </div>

                  <div className="tp-card-foot">
                    <span className="tp-card-price">
                      ₹{Number(product.price).toLocaleString('en-IN')}
                    </span>

                    <span className="d-flex align-items-center gap-1">
                      {already && (
                        <button
                          type="button"
                          className="tp-remove"
                          title="Remove from trolley"
                          onClick={() => removeFromTrolley(product._id)}
                        >
                          ✕
                        </button>
                      )}

                      <button
                        type="button"
                        className="tp-add"
                        disabled={blocked || addingId === product._id}
                        title={
                          soldOut
                            ? 'Out of stock'
                            : blocked
                              ? 'Trolley full'
                              : already
                                ? 'Add one more'
                                : 'Add to trolley'
                        }
                        onClick={(event) => {
                          const image = event.currentTarget
                            .closest('.tp-card')
                            ?.querySelector('.tp-card-img');

                          addProduct(
                            product,
                            image ? image.getBoundingClientRect() : null
                          );
                        }}
                      >
                        {addingId === product._id ? '…' : '＋'}
                      </button>
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Modal.Body>

      {/* Drop zone */}
      <Modal.Footer className="tp-foot">
        <div
          /* `is-armed` fires the moment a drag starts, so the target grows
             before the pointer gets here rather than only on hover. */
          className={`tp-trolley${draggingId && !isFull ? ' is-armed' : ''}${
            isOver && !isFull ? ' is-over' : ''
          }${isFull ? ' is-full' : ''}${justAddedId ? ' is-landed' : ''}`}
          onDragOver={onTrolleyDragOver}
          onDragLeave={() => setIsOver(false)}
          onDrop={onTrolleyDrop}
        >
          <div className="tp-trolley-left">
            <span className="tp-trolley-icon" ref={trolleyRef}>
              🛒
              {/* Burst rings when something lands */}
              <span className="tp-burst" />
              <span className="tp-burst tp-burst-2" />
            </span>

            <div>
              <div className="tp-trolley-title">
                {isFull
                  ? 'Trolley full'
                  : isOver
                    ? '🎯 Drop it in!'
                    : draggingId
                      ? '⬇ Drop anywhere in here'
                      : 'Your Trolley'}
              </div>
              <small className="tp-trolley-sub">
                {isFull
                  ? `${maxProducts} of ${maxProducts} products — remove one to add another`
                  : draggingId
                    ? 'Release to add it to your order'
                    : `${freeSlots} slot${freeSlots === 1 ? '' : 's'} free`}
              </small>
            </div>
          </div>

          <div className="tp-slots">
            {cartItems.map((item) => (
              <div
                className={`tp-slot is-filled${
                  String(item.productId) === String(justAddedId)
                    ? ' is-new'
                    : ''
                }`}
                key={item._id}
                title={`${item.name} — click to view`}
              >
                <img
                  src={item.image || PLACEHOLDER_IMG}
                  alt={item.name}
                  onError={(event) => {
                    event.currentTarget.src = PLACEHOLDER_IMG;
                  }}
                  onClick={() => {
                    const match = products.find(
                      (p) => String(p._id) === String(item.productId)
                    );
                    if (match) setPreview(match);
                  }}
                />

                <span className="tp-slot-qty">{item.quantity}</span>

                {/* Take it back out */}
                <button
                  type="button"
                  className="tp-slot-remove"
                  title={`Remove ${item.name}`}
                  disabled={removingId === item._id}
                  onClick={() => removeFromTrolley(item.productId)}
                >
                  ✕
                </button>
              </div>
            ))}

            {Array.from({ length: freeSlots }).map((_, i) => (
              <div className="tp-slot is-empty" key={`e${i}`}>
                +
              </div>
            ))}
          </div>

          <Button className="tp-done" onClick={onHide}>
            Done →
          </Button>
        </div>
      </Modal.Footer>

      {/* Product images arcing into the trolley */}
      {flyers.map((flyer) => (
        <img
          key={flyer.id}
          src={flyer.src}
          alt=""
          className="tp-flyer"
          style={{
            left: `${flyer.left}px`,
            top: `${flyer.top}px`,
            width: `${flyer.size}px`,
            height: `${flyer.size}px`,
            '--dx': `${flyer.dx}px`,
            '--dy': `${flyer.dy}px`
          }}
        />
      ))}

      <style>{trolleyPickerStyles}</style>
    </Modal>
  );
}

const trolleyPickerStyles = `
  .tp-modal {
    border: none;
    border-radius: 22px;
    overflow: hidden;
  }
  .tp-head {
    background: linear-gradient(120deg, #7c2d12, #ea580c 70%, #f59e0b);
    color: #fff;
    border: none;
  }
  .tp-head .btn-close { filter: invert(1) grayscale(1) brightness(2); }
  .tp-title { font-weight: 800; font-size: 1.15rem; }
  .tp-count {
    font-weight: 800;
    font-size: 0.86rem;
    background: rgba(255,255,255,0.18);
    border: 1px solid rgba(255,255,255,0.35);
    border-radius: 30px;
    padding: 5px 14px;
    font-variant-numeric: tabular-nums;
  }
  .tp-count.is-full { background: rgba(239,68,68,0.4); }

  /* anchors the absolutely-positioned quick-view panel */
  .tp-body { background: #fffdfa; position: relative; }

  .tp-hint {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    font-size: 0.8rem;
    color: #a16207;
    background: #fffbeb;
    border: 1px dashed #fde68a;
    border-radius: 12px;
    padding: 8px 14px;
    margin-bottom: 14px;
  }
  .tp-hint-drag { font-weight: 700; }
  .tp-hint-or { color: #d6d3d1; }

  .tp-search {
    border-radius: 50px;
    overflow: hidden;
    border: 1px solid #f1e4d0;
    background: #fff;
    max-width: 420px;
  }
  .tp-search .form-control { padding: 10px 12px; box-shadow: none !important; }

  /* Product grid */
  .tp-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    gap: 12px;
  }
  .tp-card {
    display: flex;
    flex-direction: column;
    background: #fff;
    border: 1px solid #f1e4d0;
    border-radius: 16px;
    padding: 10px;
    cursor: grab;
    position: relative;
    transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease, opacity 0.2s ease;
  }
  .tp-card:hover {
    transform: translateY(-4px);
    border-color: #fdba74;
    box-shadow: 0 12px 24px rgba(234,88,12,0.18);
  }
  .tp-card:active { cursor: grabbing; }
  /* the card being dragged */
  .tp-card.is-dragging {
    opacity: 0.45;
    transform: scale(0.96) rotate(-2deg);
    border-color: #ea580c;
  }
  .tp-card.is-blocked {
    cursor: not-allowed;
    opacity: 0.55;
  }
  .tp-card.is-blocked:hover { transform: none; box-shadow: none; }
  .tp-card.is-in { border-color: #bbf7d0; background: #f7fdf9; }

  .tp-card-in, .tp-card-out {
    position: absolute;
    top: 8px;
    left: 8px;
    z-index: 2;
    font-size: 0.62rem;
    font-weight: 800;
    border-radius: 20px;
    padding: 3px 8px;
    color: #fff;
  }
  .tp-card-in  { background: #16a34a; }
  .tp-card-out { background: #b91c1c; }

  .tp-card-img {
    position: relative;
    height: 96px;
    border-radius: 12px;
    background: #faf7f2;
    overflow: hidden;
    margin-bottom: 8px;
  }
  .tp-card-img img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    padding: 6px;
    pointer-events: none;
  }
  .tp-card-name {
    font-size: 0.82rem;
    font-weight: 700;
    color: #1c1917;
    line-height: 1.3;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    min-height: 2.1em;
    margin-bottom: 6px;
  }
  .tp-card-foot {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-top: auto;
  }
  .tp-card-price {
    font-weight: 800;
    font-size: 0.9rem;
    color: #15803d;
    font-variant-numeric: tabular-nums;
  }
  .tp-add {
    width: 30px;
    height: 30px;
    flex-shrink: 0;
    border: none;
    border-radius: 50%;
    color: #fff;
    font-size: 1rem;
    font-weight: 800;
    line-height: 1;
    background: linear-gradient(135deg, #ea580c, #f59e0b);
    box-shadow: 0 4px 10px rgba(234,88,12,0.32);
    transition: transform 0.18s ease;
  }
  .tp-add:hover:not(:disabled) { transform: scale(1.15); }
  .tp-add:disabled { background: #e7e5e4; color: #a8a29e; box-shadow: none; }

  /* Quick-view button over the card image */
  .tp-eye {
    position: absolute;
    left: 50%;
    bottom: 6px;
    transform: translate(-50%, 8px);
    white-space: nowrap;
    border: none;
    border-radius: 30px;
    padding: 4px 12px;
    font-size: 0.68rem;
    font-weight: 800;
    color: #1c1917;
    background: rgba(255,255,255,0.96);
    box-shadow: 0 4px 10px rgba(0,0,0,0.18);
    opacity: 0;
    transition: opacity 0.2s ease, transform 0.2s ease;
  }
  .tp-card:hover .tp-eye { opacity: 1; transform: translate(-50%, 0); }

  /* Remove-from-trolley button on a card */
  .tp-remove {
    width: 26px;
    height: 26px;
    flex-shrink: 0;
    border: 1px solid #fecaca;
    border-radius: 50%;
    background: #fef2f2;
    color: #b91c1c;
    font-size: 0.72rem;
    font-weight: 800;
    line-height: 1;
    transition: transform 0.18s ease, background 0.18s ease;
  }
  .tp-remove:hover { transform: scale(1.15); background: #fee2e2; }

  /* ===== Quick view panel ===== */
  .tp-preview {
    position: absolute;
    inset: 0;
    z-index: 5;
    background: #fffdfa;
    padding: 16px;
    overflow-y: auto;
    animation: tpFade 0.2s ease;
  }
  @keyframes tpFade {
    from { opacity: 0; transform: translateY(8px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .tp-preview-back {
    border: 1px solid #f1e4d0;
    background: #fff;
    color: #7c2d12;
    font-weight: 700;
    font-size: 0.82rem;
    border-radius: 30px;
    padding: 6px 16px;
    margin-bottom: 14px;
    transition: background 0.2s ease, border-color 0.2s ease;
  }
  .tp-preview-back:hover { background: #fff7ed; border-color: #fdba74; }
  .tp-preview-body {
    display: grid;
    grid-template-columns: minmax(0, 300px) minmax(0, 1fr);
    gap: 20px;
    align-items: start;
  }
  .tp-preview-img {
    background: #faf7f2;
    border: 1px solid #f1e4d0;
    border-radius: 16px;
    overflow: hidden;
    aspect-ratio: 1 / 1;
  }
  .tp-preview-img img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    padding: 14px;
  }
  .tp-preview-name {
    font-weight: 800;
    color: #1c1917;
    margin-bottom: 8px;
  }
  .tp-preview-cat, .tp-preview-stock, .tp-preview-in {
    font-size: 0.7rem;
    font-weight: 800;
    border-radius: 20px;
    padding: 4px 11px;
  }
  .tp-preview-cat   { background: #f5f5f4; color: #57534e; }
  .tp-preview-stock { background: #f0fdf4; color: #15803d; }
  .tp-preview-stock.is-out { background: #fef2f2; color: #b91c1c; }
  .tp-preview-in    { background: #ecfdf5; color: #047857; }
  .tp-preview-price {
    font-size: 1.7rem;
    font-weight: 800;
    color: #15803d;
    margin-bottom: 10px;
  }
  .tp-preview-desc {
    color: #57534e;
    font-size: 0.9rem;
    line-height: 1.6;
    margin-bottom: 8px;
  }
  .tp-preview-points {
    padding-left: 18px;
    margin: 0;
    color: #57534e;
    font-size: 0.85rem;
    line-height: 1.7;
  }
  .tp-preview-add, .tp-preview-remove {
    border: none;
    border-radius: 12px;
    font-weight: 800;
    padding: 10px 22px;
  }
  .tp-preview-add {
    background: linear-gradient(120deg, #7c2d12, #ea580c 60%, #f59e0b);
  }
  .tp-preview-add:hover:not(:disabled),
  .tp-preview-add:focus:not(:disabled) {
    background: linear-gradient(120deg, #7c2d12, #ea580c 60%, #f59e0b);
    filter: brightness(1.08);
  }
  .tp-preview-add:disabled { background: #e7e5e4; color: #a8a29e; }
  .tp-preview-remove {
    background: #fef2f2;
    color: #b91c1c;
    border: 1px solid #fecaca !important;
  }
  .tp-preview-remove:hover, .tp-preview-remove:focus {
    background: #fee2e2;
    color: #991b1b;
  }

  .tp-empty { text-align: center; padding: 40px 12px; color: #78716c; }

  /* Drop zone */
  .tp-foot {
    border: none;
    padding: 0;
    background: #fffdfa;
  }
  .tp-trolley {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 20px;
    flex-wrap: wrap;
    /* a deliberately large, easy drop target */
    min-height: 150px;
    padding: 22px 26px;
    border-top: 3px dashed #fed7aa;
    background: linear-gradient(135deg, #fffbeb, #fff7ed);
    transition: min-height 0.25s ease, background 0.25s ease,
                border-color 0.25s ease, box-shadow 0.25s ease;
  }

  /* the instant a drag begins, the target grows and announces itself */
  .tp-trolley.is-armed {
    min-height: 186px;
    border-color: #fdba74;
    background: linear-gradient(135deg, #fff7ed, #ffedd5);
    box-shadow: inset 0 0 0 3px rgba(234, 88, 12, 0.18);
    animation: tpArmed 1.4s ease-in-out infinite;
  }
  @keyframes tpArmed {
    0%, 100% { box-shadow: inset 0 0 0 3px rgba(234, 88, 12, 0.16); }
    50%      { box-shadow: inset 0 0 0 6px rgba(234, 88, 12, 0.30); }
  }

  /* pointer actually over it */
  .tp-trolley.is-over {
    min-height: 200px;
    background: linear-gradient(135deg, #fef3c7, #ffedd5);
    border-color: #ea580c;
    box-shadow: inset 0 0 0 4px rgba(234, 88, 12, 0.45);
    animation: none;
  }
  .tp-trolley.is-over .tp-trolley-icon {
    transform: scale(1.3) rotate(-8deg);
    box-shadow: 0 16px 32px rgba(234,88,12,0.45);
  }
  .tp-trolley.is-full { border-color: #fecaca; }
  /* ===== The product flying into the trolley ===== */
  .tp-flyer {
    position: fixed;
    z-index: 2000;
    pointer-events: none;
    object-fit: cover;
    border-radius: 14px;
    border: 2px solid #fff;
    box-shadow: 0 14px 30px rgba(15, 23, 42, 0.35);
    /* lobbed up, then dropped into the basket */
    animation: tpFly 0.8s cubic-bezier(0.4, 0, 0.6, 1) forwards;
  }
  @keyframes tpFly {
    0% {
      transform: translate(0, 0) scale(1) rotate(0deg);
      opacity: 1;
    }
    25% {
      transform:
        translate(calc(var(--dx) * 0.25), calc(var(--dy) * 0.25 - 90px))
        scale(0.85) rotate(-14deg);
      opacity: 1;
    }
    60% {
      transform:
        translate(calc(var(--dx) * 0.65), calc(var(--dy) * 0.65 - 70px))
        scale(0.6) rotate(10deg);
      opacity: 1;
    }
    88% {
      transform:
        translate(var(--dx), calc(var(--dy) + 2px)) scale(0.2) rotate(18deg);
      opacity: 0.9;
    }
    100% {
      transform: translate(var(--dx), var(--dy)) scale(0.08) rotate(22deg);
      opacity: 0;
    }
  }

  /* the trolley takes the hit */
  .tp-trolley.is-landed .tp-trolley-icon { animation: tpLand 0.7s ease; }
  @keyframes tpLand {
    0%   { transform: translateY(0) scale(1) rotate(0deg); }
    18%  { transform: translateY(-4px) scale(1.06) rotate(-4deg); }
    /* impact — squashes as the product drops in */
    42%  { transform: translateY(4px) scale(1.22, 0.82) rotate(3deg); }
    62%  { transform: translateY(-8px) scale(0.94, 1.1) rotate(-6deg); }
    80%  { transform: translateY(0) scale(1.04, 0.98) rotate(2deg); }
    100% { transform: translateY(0) scale(1) rotate(0deg); }
  }

  /* expanding rings at the point of impact */
  .tp-burst {
    position: absolute;
    inset: 0;
    border-radius: 16px;
    border: 2px solid #ea580c;
    opacity: 0;
    pointer-events: none;
  }
  .tp-trolley.is-landed .tp-burst {
    animation: tpBurst 0.7s ease-out;
  }
  .tp-trolley.is-landed .tp-burst-2 {
    animation-delay: 0.12s;
    border-color: #fbbf24;
  }
  @keyframes tpBurst {
    0%   { transform: scale(0.7); opacity: 0.85; }
    100% { transform: scale(2.1); opacity: 0; }
  }

  .tp-trolley-left {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
  }
  .tp-trolley-icon {
    position: relative;
    width: 82px;
    height: 82px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 2.6rem;
    border-radius: 22px;
    background: #fff;
    border: 1px solid #fed7aa;
    box-shadow: 0 6px 14px rgba(234,88,12,0.2);
    /* squash/stretch on impact reads better from the base */
    transform-origin: 50% 100%;
    transition: transform 0.25s ease, box-shadow 0.25s ease;
  }
  .tp-trolley-title {
    font-weight: 800;
    font-size: 1.2rem;
    color: #7c2d12;
    line-height: 1.2;
  }
  .tp-trolley-sub { color: #a16207; font-size: 0.88rem; }

  .tp-slots {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    flex: 1 1 auto;
  }
  .tp-slot {
    position: relative;
    width: 74px;
    height: 74px;
    flex-shrink: 0;
    border-radius: 16px;
    transition: width 0.25s ease, height 0.25s ease;
  }
  .tp-slot.is-filled {
    background: #fff;
    border: 1px solid #fed7aa;
    box-shadow: 0 4px 10px rgba(124,45,18,0.10);
  }
  .tp-slot.is-filled img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 15px;
    display: block;
    cursor: pointer;
  }
  /* Remove control on a filled slot */
  .tp-slot-remove {
    position: absolute;
    left: -7px;
    top: -7px;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 2px solid #fffbeb;
    border-radius: 50%;
    background: #dc2626;
    color: #fff;
    font-size: 0.7rem;
    font-weight: 800;
    line-height: 1;
    padding: 0;
    cursor: pointer;
    opacity: 0;
    transform: scale(0.7);
    transition: opacity 0.18s ease, transform 0.18s ease;
  }
  .tp-slot.is-filled:hover .tp-slot-remove { opacity: 1; transform: scale(1); }
  .tp-slot-remove:hover { background: #b91c1c; transform: scale(1.15); }
  .tp-slot-remove:disabled { opacity: 0.5; }
  /* the slot that just received a product */
  .tp-slot.is-new { animation: tpPop 0.6s cubic-bezier(0.22, 1, 0.36, 1); }
  @keyframes tpPop {
    0%   { transform: scale(0.2) translateY(-26px); opacity: 0; }
    60%  { transform: scale(1.15) translateY(0); opacity: 1; }
    100% { transform: scale(1); }
  }
  .tp-slot-qty {
    position: absolute;
    top: -6px;
    right: -6px;
    min-width: 22px;
    height: 22px;
    padding: 0 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 30px;
    background: linear-gradient(135deg, #ea580c, #f59e0b);
    color: #fff;
    font-size: 0.72rem;
    font-weight: 800;
    border: 2px solid #fffbeb;
  }
  .tp-slot.is-empty {
    display: flex;
    align-items: center;
    justify-content: center;
    border: 3px dashed #fdba74;
    background: rgba(255,255,255,0.6);
    color: #ea580c;
    font-size: 1.8rem;
    font-weight: 700;
  }
  /* free slots pulse while a drag is in progress */
  .tp-trolley.is-armed .tp-slot.is-empty,
  .tp-trolley.is-over .tp-slot.is-empty {
    border-color: #ea580c;
    background: #fff;
    animation: tpSlotPulse 1.2s ease-in-out infinite;
  }
  @keyframes tpSlotPulse {
    0%, 100% { transform: scale(1); }
    50%      { transform: scale(1.08); }
  }

  .tp-done {
    border: none;
    border-radius: 14px;
    font-weight: 800;
    font-size: 1.02rem;
    padding: 14px 30px;
    background: linear-gradient(120deg, #15803d, #22c55e);
    margin-left: auto;
  }
  .tp-done:hover, .tp-done:focus {
    background: linear-gradient(120deg, #166534, #16a34a);
  }

  /* Skeletons */
  .tp-skel-card { cursor: default; }
  .tp-skel-card:hover { transform: none; box-shadow: none; }
  .tp-skel {
    display: block;
    background: linear-gradient(90deg, #f1ece4 25%, #faf7f2 37%, #f1ece4 63%);
    background-size: 400% 100%;
    animation: tpShimmer 1.4s ease infinite;
    border-radius: 8px;
  }
  .tp-skel-img { height: 96px; border-radius: 12px; margin-bottom: 10px; }
  .tp-skel-line { height: 11px; margin-bottom: 7px; }
  .tp-skel-line.short { width: 55%; }
  @keyframes tpShimmer {
    0%   { background-position: 100% 0; }
    100% { background-position: -100% 0; }
  }

  @media (max-width: 767.98px) {
    .tp-preview-body { grid-template-columns: 1fr; }
    .tp-preview-img { max-width: 260px; margin-inline: auto; }
    /* no hover on touch — keep the controls visible */
    .tp-eye, .tp-slot-remove { opacity: 1; transform: none; }
    .tp-eye { position: static; transform: none; display: block; margin: 6px auto 0; }
  }

  @media (max-width: 575.98px) {
    .tp-grid { grid-template-columns: repeat(auto-fill, minmax(128px, 1fr)); }
    .tp-trolley { gap: 12px; padding: 14px; min-height: 0; }
    .tp-trolley.is-armed, .tp-trolley.is-over { min-height: 0; }
    .tp-trolley-icon { width: 58px; height: 58px; font-size: 1.8rem; border-radius: 18px; }
    .tp-trolley-title { font-size: 1rem; }
    .tp-slot { width: 56px; height: 56px; }
    .tp-done { width: 100%; margin-left: 0; padding: 12px 20px; }
  }

  /* Short viewports: don't let the drop zone crowd out the product grid */
  @media (max-height: 720px) {
    .tp-trolley { min-height: 118px; padding: 16px 20px; }
    .tp-trolley.is-armed { min-height: 138px; }
    .tp-trolley.is-over  { min-height: 148px; }
    .tp-trolley-icon { width: 64px; height: 64px; font-size: 2rem; }
    .tp-slot { width: 60px; height: 60px; }
  }

  @media (prefers-reduced-motion: reduce) {
    .tp-card, .tp-trolley, .tp-trolley-icon, .tp-add, .tp-skel,
    .tp-slot.is-new, .tp-eye, .tp-slot-remove, .tp-remove, .tp-preview,
    .tp-flyer, .tp-burst {
      transition: none;
      animation: none;
    }
    /* the flight is skipped entirely at the JS level too */
    .tp-flyer { display: none; }
  }
`;

export default TrolleyPicker;
